import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { breakpoint } from '@zocdoc/components/src/grid';
import { FontFamily } from '@zocdoc/components/src/typography';
import { navy } from '@zocdoc/branding/colors';
import Stars from '@zocdoc/components/src/stars';
import InfoToolTip from '../../../../Shared/InfoTooltip/view';
import { debounceTooltipTracking } from '../../utils/debounceTooltipTracking';
import {
    LOCALIZED_STRINGS,
    OVERALL_RATING_STRINGS,
    BEDSIDE_MANNER_STRINGS,
    WAIT_TIME_STRINGS,
    RATING_MESSAGE,
} from '../constants';

export const PatientReviewsText = styled.div`
    font: 18px/24px ${FontFamily.SharpSansBold};
    color: ${({ theme }) => theme.profile.textColor};
    display: inline-block;
    margin-bottom: 10px;

    ${breakpoint.medium} {
        font-size: 20px;
    }
`;

export const ReviewsLegend = styled.div`
    font: 14px/20px ${FontFamily.KievitSlabBook};
    color: ${({ theme }) => theme.profile.textColor};
    ${breakpoint.medium} {
        font: 18px/28px ${FontFamily.KievitSlabBook};
    }
`;

const CumulativeRatingWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-top: 8px;

    ${breakpoint.medium} {
        margin-top: 12px;
    }
`;

const CumulativeRatingText = styled.div`
    font: 18px/24px ${FontFamily.SharpSansBold};
    color: ${({ theme }) => theme.profile.textColor};
    display: inline-block;
    margin-left: 33px;
`;

const StarsWrapper = styled.div`
    display: inline-block;
    font-size: 12px;

    ${breakpoint.medium} {
        font-size: 20px;
    }
`;

const CumulativeRatingDiv = styled.div`
    margin: 25px 0 25px;
`;

const ReviewCount = styled.div`
    color: ${navy};
    font: 13px/16px ${FontFamily.KievitSlabBook};
    padding-bottom: 31px;
    ${breakpoint.medium} {
        font-size: 16px;
        line-height: 19px;
    }
`;

export default class ReviewsHeader extends PureComponent {
    static propTypes = {
        reviews: PropTypes.array.isRequired,
    }

    renderCumulativeRatings() {
        const {
            rating,
            reviews,
            reviewCount,
            trackClickIBubbleOverall,
            trackClickIBubbleWaitTime,
            trackClickIBubbleBedsideManner,
        } = this.props;
        let waitTime = 0;
        let bedside = 0;
        let numWaitTimeScores = 0;
        let numBedsideScores = 0;
        let reviewMetadata = '';

        reviews.forEach(review => {
            if (review.waitTimeRating) {
                waitTime += review.waitTimeRating;
                numWaitTimeScores++;
            }

            if (review.bedsideRating) {
                bedside += review.bedsideRating;
                numBedsideScores++;
            }
        });

        let avgWaitTimeScore;
        let avgBedsideScore;

        if (rating > 0) {
            reviewMetadata = [
                <meta
                    key="ratingValue"
                    content={rating.toFixed(1)}
                    itemProp="ratingValue"
                />,
                <meta
                    key="worstRating"
                    content="1"
                    itemProp="worstRating"
                />,
                <meta
                    key="bestRating"
                    content="5"
                    itemProp="bestRating"
                />,
                <meta
                    key="reviewCount"
                    content={reviewCount}
                    itemProp="reviewCount"
                />,
            ];
        }
        if (numWaitTimeScores > 0) {
            avgWaitTimeScore = waitTime / numWaitTimeScores;
        }
        if (numBedsideScores > 0) {
            avgBedsideScore = bedside / numBedsideScores;
        }

        return (
            <CumulativeRatingDiv
                itemProp="aggregateRating"
                itemScope
                itemType="http://schema.org/AggregateRating"
            >
                {reviewMetadata}
                {this.renderCumulativeRating(OVERALL_RATING_STRINGS, rating, trackClickIBubbleOverall)}
                {this.renderCumulativeRating(WAIT_TIME_STRINGS, avgWaitTimeScore, trackClickIBubbleWaitTime)}
                {this.renderCumulativeRating(BEDSIDE_MANNER_STRINGS, avgBedsideScore, trackClickIBubbleBedsideManner)}
            </CumulativeRatingDiv>
        );
    }

    renderCumulativeRating(stringSet, score, tooltipTrackingFn) {
        if (score) {
            return (
                <CumulativeRatingWrapper>
                    <StarsWrapper>
                        <Stars rating={score} />
                    </StarsWrapper>
                    <CumulativeRatingText>
                        <FormattedMessage {...stringSet.Label} />
                    </CumulativeRatingText>
                    <InfoToolTip onDisplayTooltip={debounceTooltipTracking(tooltipTrackingFn)}>
                        <p><FormattedMessage {...RATING_MESSAGE} /></p>
                        <br />
                        <p><FormattedMessage {...stringSet.Tooltip} /></p>
                        <br />
                        <p><FormattedMessage {...stringSet.Stars5} /></p>
                        <p><FormattedMessage {...stringSet.Stars4} /></p>
                        <p><FormattedMessage {...stringSet.Stars3} /></p>
                        <p><FormattedMessage {...stringSet.Stars2} /></p>
                        <p><FormattedMessage {...stringSet.Stars1} /></p>
                    </InfoToolTip>
                </CumulativeRatingWrapper>
            );
        }
        return (<div></div>);
    }

    maybeRenderReviewCount() {
        if (this.props.shouldRenderReviewCount) {
            return (
                <ReviewCount>
                    <FormattedMessage
                        {...LOCALIZED_STRINGS.BasedOnReviews}
                        values={{ numberOfReviews: this.props.reviewCount }}
                    />
                </ReviewCount>
            );
        }
    }

    render() {
        return (
            <div>
                <PatientReviewsText>
                    <FormattedMessage {...LOCALIZED_STRINGS.ReviewsHeader} />
                </PatientReviewsText>
                <ReviewsLegend>
                    <FormattedMessage
                        {...LOCALIZED_STRINGS.ReviewsLegend}
                    />
                </ReviewsLegend>
                {this.renderCumulativeRatings()}
                {this.maybeRenderReviewCount()}
            </div>
        );
    }
}
